import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import styles from '../styles/Footer.module.css';
import Toast, { ToastProps } from './Toast';
import AudiatedLogo from './AudiatedLogo';
import { ROUTES } from '../config/routes';

/**
 * Footer component with brand information, community links, and newsletter signup
 * Features floating musical instruments animation and responsive 3-column layout
 */
interface FooterProps {
    /** Optional custom className for additional styling */
    className?: string;
}

interface FormData {
    email: string;
}

const Footer: React.FC<FooterProps> = ({ className = '' }) => {
    const [formData, setFormData] = useState<FormData>({
        email: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastProps>({
        message: '',
        type: 'success',
        isVisible: false,
        onClose: () => {}
    });

    const showToast = useCallback((message: string, type: 'success' | 'error') => {
        setToast({
            message,
            type,
            isVisible: true,
            onClose: () => setToast(prev => ({ ...prev, isVisible: false }))
        });
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            email: e.target.value
        }));
    }, []);

    const handleSubscribe = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Early return if already submitting or empty email
        if (isSubmitting || !formData.email.trim()) {
            return;
        }

        const emailToSubmit = formData.email.trim();

        // Start brewing animation
        setIsSubmitting(true);

        // Cohesive timing: after 600ms, clear email + show toast + stop brewing
        setTimeout(() => {
            setFormData({ email: '' });
            showToast('Thanks for joining the musical journey!', 'success');
            setIsSubmitting(false);
        }, 600);

        // Submit to Google Sheets in background (doesn't affect UI)
        try {
            const scriptURL = 'https://script.google.com/macros/s/AKfycbyxD37k7Tvuesvte4V5ekHWXs3c098QEVcbwO3Kt-HJsqF4IDXlep9e7huCCe-NhoOb/exec';
            const form = new FormData();
            form.append('email', emailToSubmit);

    
            const response = await fetch(scriptURL, {
                method: 'POST',
                body: form
            });

            const responseText = await response.text();

            // Parse JSON response
            let jsonResponse;
            try {
                jsonResponse = JSON.parse(responseText);

                if (jsonResponse.result === 'error') {
                    console.error('❌ Google Script Error:', jsonResponse.error);
                    throw new Error(`Script error: ${JSON.stringify(jsonResponse.error)}`);
                }
            } catch (parseError) {
                console.error('❌ Failed to parse response:', parseError);
            }

            if (!response.ok || jsonResponse?.result !== 'success') {
                throw new Error('Failed to submit');
            }

    
        } catch (error) {
            console.error('Subscription error:', error);
            // Silently fail - user already saw success. Could implement retry logic here.
        }
    }, [isSubmitting, formData.email, showToast]);

    return (
        <footer className={`${styles.footer} ${className}`}>
            {/* Animated Musical Notes Background - decorative elements */}
            <div className={styles.musicalNotes} aria-hidden="true">
                <span className={`${styles.note} ${styles.note1}`} aria-hidden="true">♪</span>
                <span className={`${styles.note} ${styles.note2}`} aria-hidden="true">♫</span>
                <span className={`${styles.note} ${styles.note3}`} aria-hidden="true">♬</span>
                <span className={`${styles.note} ${styles.note4}`} aria-hidden="true">♭</span>
            </div>

            <div className={styles.footerContent}>
                {/* Main Footer Grid */}
                <div className={styles.footerGrid}>
                    {/* Brand Column with Floating Musical Instruments */}
                    <div className={styles.brandColumn}>
                        <div className={styles.brand}>
                            <AudiatedLogo variant="footer" />
                        </div>
                        <p className={styles.brandTagline}>
                            Where every note tells a story 🎵
                        </p>
                        <p className={styles.brandDescription}>
                            Join thousands of musicians discovering the joy of playing by ear.
                        </p>

                        {/* Floating Musical Instruments - decorative animation */}
                        <div className={styles.musicalFloatContainer} aria-hidden="true">
                            <div className={`${styles.floatingElement} ${styles.instrument1}`} role="presentation">🎸</div>
                            <div className={`${styles.floatingElement} ${styles.instrument2}`} role="presentation">🎹</div>
                            <div className={`${styles.floatingElement} ${styles.instrument3}`} role="presentation">🎻</div>
                            <div className={`${styles.floatingElement} ${styles.instrument4}`} role="presentation">🥁</div>
                            <div className={`${styles.floatingElement} ${styles.instrument5}`} role="presentation">🎷</div>
                            <div className={`${styles.floatingElement} ${styles.instrument6}`} role="presentation">🎺</div>
                            <div className={`${styles.floatingElement} ${styles.instrument7}`} role="presentation">🎤</div>
                            <div className={`${styles.floatingElement} ${styles.instrument8}`} role="presentation">🔊</div>
                        </div>
                    </div>

                    {/* Stacked Community & Discover Column */}
                    <div className={styles.stackedColumn}>
                        {/* Community Section */}
                        <div className={styles.communitySection}>
                            <h3 className={styles.columnHeader}>
                                <span className={styles.columnIcon}>🎸</span>
                                Community
                            </h3>
                            <div className={styles.communityGrid}>
                                <a
                                    href="https://www.facebook.com/groups/889133896900553"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.featuredLink}
                                >
                                    <span className={styles.linkIcon}>👥</span>
                                    <div className={styles.linkContent}>
                                        <span className={styles.linkTitle}>Facebook Group</span>
                                        <span className={styles.linkDescription}>Join our community</span>
                                    </div>
                                </a>
                                <a
                                    href="https://discord.gg/C4AYdF2w2e"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={styles.featuredLink}
                                >
                                    <span className={styles.linkIcon}>🎮</span>
                                    <div className={styles.linkContent}>
                                        <span className={styles.linkTitle}>Discord Server</span>
                                        <span className={styles.linkDescription}>Chat with musicians</span>
                                    </div>
                                </a>
                            </div>
                        </div>

                        {/* Discover Section - 2x2 Grid */}
                        <div className={styles.discoverSection}>
                            <h3 className={styles.columnHeader}>
                                <span className={styles.columnIcon}>🏢</span>
                                Discover
                            </h3>
                            <div className={styles.discoverGrid}>
                                <a href="/about" className={styles.featuredLink}>
                                    <span className={styles.linkIcon}>📖</span>
                                    <div className={styles.linkContent}>
                                        <span className={styles.linkTitle}>About Us</span>
                                        <span className={styles.linkDescription}>Our story</span>
                                    </div>
                                </a>
                                <a href="/success-stories" className={styles.featuredLink}>
                                    <span className={styles.linkIcon}>⭐</span>
                                    <div className={styles.linkContent}>
                                        <span className={styles.linkTitle}>Success Stories</span>
                                        <span className={styles.linkDescription}>Student achievements</span>
                                    </div>
                                </a>
                                <Link to={ROUTES.FEEDBACK} className={styles.featuredLink}>
                                    <span className={styles.linkIcon}>📬</span>
                                    <div className={styles.linkContent}>
                                        <span className={styles.linkTitle}>Contact Us</span>
                                        <span className={styles.linkDescription}>Get in touch</span>
                                    </div>
                                </Link>
                                <Link to={ROUTES.FEEDBACK} className={styles.featuredLink}>
                                    <span className={styles.linkIcon}>💬</span>
                                    <div className={styles.linkContent}>
                                        <span className={styles.linkTitle}>Share Feedback</span>
                                        <span className={styles.linkDescription}>Help us improve</span>
                                    </div>
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* Newsletter Column */}
                    <div className={styles.newsletterColumn}>
                        <h3 className={styles.columnHeader}>
                            <span className={styles.columnIcon}>📧</span>
                            Musical Goodies
                        </h3>
                        <p className={styles.newsletterDescription}>
                            Weekly tips, tricks, and musical magic delivered to your inbox!
                        </p>
                        <form onSubmit={handleSubscribe} className={styles.subscribeForm} noValidate>
                            <label htmlFor="footer-email" className={styles.visuallyHidden}>
                                Email address for newsletter signup
                            </label>
                            <input
                                id="footer-email"
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                placeholder="Enter your email"
                                className={styles.subscribeInput}
                                required
                                aria-label="Email address for newsletter signup"
                                aria-describedby="email-help"
                            />
                            <small id="email-help" className={styles.visuallyHidden}>
                                Enter your email address to subscribe to our newsletter for weekly musical tips and updates.
                            </small>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={styles.subscribeButton}
                                aria-describedby="subscribe-status"
                            >
                                {isSubmitting ? '🎵 Brewing...' : '🚀 Subscribe'}
                            </button>
                        </form>
                    </div>
                </div>

  
                {/* Footer Bottom */}
                <div className={styles.footerBottom}>
                    <div className={styles.bottomContent}>
                        <p className={styles.copyright}>Made with 💜 for music lovers everywhere</p>
                        <div className={styles.musicalEmoji}>🎵🎶🎤🎸🥁🎹</div>
                    </div>
                </div>
            </div>

            {/* Toast Notification */}
            <Toast
                message={toast.message}
                type={toast.type}
                isVisible={toast.isVisible}
                onClose={toast.onClose}
            />
        </footer>
    );
};

export default Footer;